import { createTheme } from "@mui/material";
import { red } from "@mui/material/colors";

/**
 * Enhanced theme configuration for professional appearance
 * @param {*} currentLanguage
 * @returns
 */
const PALETTES = {
  light: {
    mode: "light",
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
    primary: {
      main: "#0d2fa1",
      light: "#4d5db5",
      dark: "#091f70",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#1976d2",
      light: "#42a5f5",
      dark: "#1565c0",
    },
    success: {
      main: "#2e7d32",
      light: "#4caf50",
      dark: "#1b5e20",
    },
    warning: {
      main: "#ed6c02",
      light: "#ff9800",
      dark: "#e65100",
    },
    error: {
      main: "#d32f2f",
      light: "#ef5350",
      dark: "#c62828",
    },
    text: {
      primary: "#1a2027",
      secondary: "#3e5060",
    },
  },
  dark: {
    mode: "dark",
    background: {
      default: "#0f172a",
      paper: "#1e293b",
    },
    primary: {
      main: "#3399ff",
      light: "#66b2ff",
      dark: "#0072e5",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#90caf9",
      light: "#b3dcf2",
      dark: "#5d99c6",
    },
    success: {
      main: "#1db584",
      light: "#1dd1a1",
      dark: "#1aa251",
    },
    warning: {
      main: "#ffc947",
      light: "#ffdc48",
      dark: "#f57c00",
    },
    error: {
      main: "#f44336",
      light: "#f6685e",
      dark: "#aa2e25",
    },
    text: {
      primary: "#ffffff",
      secondary: "#b2bac2",
    },
  },
};

export default function createMuiTheme(
  currentLanguage,
  mode = "dark",
  direction = "ltr",
) {
  return createTheme(
    {
      direction,
      palette: PALETTES[mode] || PALETTES.dark,
      typography: {
        fontFamily: [
          "Plus Jakarta Sans",
          "Source Sans Pro",
          "-apple-system",
          "BlinkMacSystemFont",
          "Segoe UI",
          "Roboto",
          "Helvetica Neue",
          "Arial, sans-serif",
          "Apple Color Emoji",
          "Segoe UI Emoji",
          "Segoe UI Symbol",
        ].join(","),
        allVariants: {
          textTransform: "inherit",
          fontFeatureSettings: '"cv11", "ss01"',
        },
        h1: {
          fontSize: "2.5rem",
          fontWeight: 700,
          lineHeight: 1.2,
        },
        h2: {
          fontSize: "2rem",
          fontWeight: 600,
          lineHeight: 1.3,
        },
        h3: {
          fontSize: "1.75rem",
          fontWeight: 600,
          lineHeight: 1.4,
        },
        h4: {
          fontSize: "1.5rem",
          fontWeight: 600,
          lineHeight: 1.4,
        },
        h5: {
          fontSize: "1.25rem",
          fontWeight: 600,
          lineHeight: 1.5,
        },
        h6: {
          fontSize: "1.125rem",
          fontWeight: 600,
          lineHeight: 1.6,
        },
        body1: {
          fontSize: "1rem",
          lineHeight: 1.7,
        },
        body2: {
          fontSize: "0.875rem",
          lineHeight: 1.6,
        },
        button: {
          fontSize: "0.875rem",
          fontWeight: 500,
          textTransform: "none",
          letterSpacing: "0.25px",
        },
      },
      components: {
        MuiAppBar: {
          styleOverrides: {
            root: ({ theme }) => ({
              backgroundImage: "none",
              backdropFilter: "blur(20px)",
              borderBottom: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.12)"
                  : "rgba(0, 0, 0, 0.08)"
              }`,
            }),
          },
        },
        MuiToolbar: {
          styleOverrides: {
            root: {
              minHeight: "72px",
              "@media (min-width:600px)": {
                minHeight: "72px",
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: ({ theme }) => ({
              borderRadius: theme.shape.borderRadius,
              textTransform: "none",
              fontWeight: 500,
              boxShadow: "none",
              "&:hover": {
                boxShadow: `0 2px 8px ${theme.palette.primary.main}25`,
              },
            }),
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: ({ theme }) => ({
              borderRadius: theme.shape.borderRadius,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 4px 20px rgba(0, 0, 0, 0.5)"
                  : "0 4px 20px rgba(0, 0, 0, 0.1)",
            }),
          },
        },
        MuiCard: {
          styleOverrides: {
            root: ({ theme }) => ({
              borderRadius: theme.shape.borderRadius,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 4px 20px rgba(0, 0, 0, 0.3)"
                  : "0 2px 12px rgba(0, 0, 0, 0.08)",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.12)"
                  : "rgba(0, 0, 0, 0.08)"
              }`,
            }),
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: ({ theme }) => ({
              borderRadius: theme.shape.borderRadius,
              transition: theme.transitions.create(
                ["background-color", "transform"],
                {
                  duration: theme.transitions.duration.short,
                },
              ),
              "&:hover": {
                transform: "scale(1.05)",
              },
            }),
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              fontSize: "1rem",
              fontWeight: 500,
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            asterisk: {
              "&$error": {
                color: red[500],
              },
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: ({ theme }) => ({
              fontSize: "1rem",
              fontWeight: 500,
              textTransform: "none",
              minHeight: "48px",
              "&.Mui-selected": {
                fontWeight: 600,
              },
            }),
          },
        },
        MuiTabs: {
          styleOverrides: {
            root: {
              overflow: "auto",
            },
            indicator: ({ theme }) => ({
              height: 3,
              borderRadius: "3px 3px 0 0",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            }),
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: ({ theme }) => ({
              "& .MuiOutlinedInput-root": {
                borderRadius: theme.shape.borderRadius,
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.light,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 2,
                  borderColor: theme.palette.primary.main,
                },
              },
            }),
          },
        },
      },
    },
    currentLanguage,
  );
}
